import type { MeResponse } from '@ucp/web/lib';
import { hasAnyRole } from '../shell/navConfig';

/**
 * The account-scoped Profile Settings sections (PRO-0740, ADR-014 amendment).
 * `my-coverage` re-homes the Referrals My Coverage editor; `service-profiles`
 * re-homes the Referrals Service Profiles editor.
 */
export type ProfileSettingsSectionId = 'my-coverage' | 'service-profiles';

/**
 * Single source of truth for which Profile Settings sections apply to a user,
 * shared by `ProfileSettingsPage` (what to render) and `AccountMenu` (whether to
 * show the drawer item) so the two cannot drift (CF-1).
 *
 * Role-scoped, NOT capability-scoped (MF-1) — the shell has no client capability
 * engine, so we gate by the roles verified to hold each referral capability, plus
 * the same sub-flag each editor's route already checks (mirrors `ReferralsSurface`):
 * - My Coverage → `REFERRAL_REVIEWER` (holds `referral_review`) + `referralsRoutingEnabled`.
 * - Service Profiles → `ADMISSIONS_DIRECTOR` (holds `referral_oversight_read`) +
 *   `referralsScoringCalibrationEnabled`.
 *
 * The server stays authoritative for every data call regardless of this client gate
 * (sub-flag 503 → capability 403 → central-scope RLS). `L6_PLATFORM_ADMIN` holds
 * neither referral capability, so it yields `[]` (EoP preserved).
 *
 * Drift note: if `apps/api/src/auth/capabilities.ts` changes which role holds
 * `referral_review` / `referral_oversight_read`, update the role literals here in
 * lockstep (the server stays authoritative regardless).
 */
export function profileSettingsSections(user: MeResponse): ProfileSettingsSectionId[] {
  const out: ProfileSettingsSectionId[] = [];
  if (hasAnyRole(user, ['REFERRAL_REVIEWER']) && user.featureFlags.referralsRoutingEnabled) {
    out.push('my-coverage');
  }
  if (
    hasAnyRole(user, ['ADMISSIONS_DIRECTOR']) &&
    user.featureFlags.referralsScoringCalibrationEnabled
  ) {
    out.push('service-profiles');
  }
  return out;
}
